// src/components/elevenlabs/TTSPage.tsx
// Text-to-Speech — sync generation with voice/model picker + sliders.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Mic, Play, Volume2,
} from 'lucide-react';

import {
  ApiError, listVoices, listModels, textToSpeech,
  type ELVoice, type ELModel, type TTSIn,
} from '@/lib/api_elevenlabs';
import { AudioPlayer } from '@/components/shared/AudioPlayer';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function TTSPage({ addNotification }: { addNotification?: Notifier }) {
  const [voices, setVoices] = useState<ELVoice[]>([]);
  const [models, setModels] = useState<ELModel[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  // Form state
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [modelId, setModelId] = useState('');
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [style, setStyle] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [generating, setGenerating] = useState(false);

  // Result
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Preview playback
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setApiMissing(false);
    try {
      const [v, m] = await Promise.all([listVoices(), listModels()]);
      setVoices(v);
      setModels(m);
      if (v.length && !voiceId) setVoiceId(v[0].voice_id);
      if (m.length && !modelId) setModelId(m[0].model_id);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) {
        setApiMissing(true);
      } else {
        addNotification?.({ type: 'error', title: 'Error cargando voces/modelos', message: e.message });
      }
    } finally {
      setLoadingMeta(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const voicesByCategory = useMemo(() => {
    const map: Record<string, ELVoice[]> = {};
    for (const v of voices) {
      const cat = v.category || 'other';
      (map[cat] ??= []).push(v);
    }
    return map;
  }, [voices]);

  const handleGenerate = async () => {
    if (!text.trim() || !voiceId) return;
    setGenerating(true);
    setAudioUrl(null);
    try {
      const payload: TTSIn = {
        text: text.trim(),
        voice_id: voiceId,
        ...(modelId ? { model_id: modelId } : {}),
        stability,
        similarity_boost: similarity,
        style,
        speed,
      };
      const res = await textToSpeech(payload);
      setAudioUrl(res.audio_url);
      addNotification?.({ type: 'success', title: 'Audio generado', message: `Duración: ${res.duration.toFixed(1)}s` });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error generando audio', message: e.message });
    } finally {
      setGenerating(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="ElevenLabs" />;

  if (loadingMeta) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={24} className="animate-spin text-white/40" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Text */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Texto a convertir</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)] transition-colors resize-y"
          placeholder="Escribe o pega el texto que deseas convertir a audio…"
        />
        <p className="text-[11px] text-white/30 mt-1">{text.length} caracteres</p>
      </div>

      {/* Voice picker */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Voz</label>
        <div className="flex items-center gap-2">
          <select
            value={voiceId}
            onChange={(e) => setVoiceId(e.target.value)}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--acc1)]"
          >
            {Object.entries(voicesByCategory).map(([cat, vs]) => (
              <optgroup key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                {vs.map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}{v.language ? ` (${v.language})` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {/* Preview play */}
          {(() => {
            const sel = voices.find((v) => v.voice_id === voiceId);
            if (!sel?.preview_url) return null;
            return (
              <button
                type="button"
                onClick={() => setPreviewSrc(previewSrc === sel.preview_url ? null : sel.preview_url!)}
                className="p-2 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:text-white/80"
                title="Vista previa"
              >
                <Play size={14} />
              </button>
            );
          })()}
        </div>
        {previewSrc && <audio src={previewSrc} autoPlay onEnded={() => setPreviewSrc(null)} className="hidden" />}
      </div>

      {/* Model picker */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Modelo</label>
        <select
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--acc1)]"
        >
          {models.map((m) => (
            <option key={m.model_id} value={m.model_id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-2 gap-4">
        <Slider label="Estabilidad" value={stability} onChange={setStability} min={0} max={1} step={0.05} />
        <Slider label="Similitud" value={similarity} onChange={setSimilarity} min={0} max={1} step={0.05} />
        <Slider label="Estilo" value={style} onChange={setStyle} min={0} max={1} step={0.05} />
        <Slider label="Velocidad" value={speed} onChange={setSpeed} min={0.5} max={2} step={0.1} />
      </div>

      {/* Submit */}
      <button
        onClick={handleGenerate}
        disabled={generating || !text.trim() || !voiceId}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />}
        {generating ? 'Generando…' : 'Generar audio'}
      </button>

      {/* Result */}
      {audioUrl && (
        <div className="o-card border border-emerald-500/30">
          <p className="text-xs text-white/50 mb-2 flex items-center gap-1.5">
            <Mic size={12} /> Audio generado
          </p>
          <AudioPlayer src={audioUrl} />
        </div>
      )}
    </div>
  );
}

// ── Slider primitive ──────────────────────────────────────────

function Slider({
  label, value, onChange, min, max, step,
}: {
  label: string; value: number; onChange: (v: number) => void;
  min: number; max: number; step: number;
}) {
  return (
    <label className="block">
      <span className="text-xs text-white/50 flex justify-between">
        {label}
        <span className="text-white/70 font-mono">{value.toFixed(2)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1 accent-[var(--acc1)]"
      />
    </label>
  );
}
